import classNames from 'classnames';
import dynamic from 'next/dynamic';
import NextImage from 'next/image';
import React, { ComponentType } from 'react';
import SvgArrowDown from './icons/SvgArrowDown';
import { Button } from './Button';
import { GeolocPosition, useGeolocPosition } from './vendors/useGeolocPosition';
import { getMapCenter, getMapOptions, makeMapMarker } from '../src/utils/map';
import { GoogleMap } from '@react-google-maps/api';
import { ProductRackFragment, VendorFragment } from '../src/model/fragments';
import { useFetchProductRackVendors } from '../src/hooks/useFetchProductRackVendors';
import { useLocalization } from '../src/hooks/useLocaleFormatter';
import { useTranslate } from '../src/hooks/useTranslate';
import { VendorInfoWindow } from './vendors/VendorInfoWindow';
import { ProductRackList } from './ProductRackList';


interface ProductRackMapProps {
    racks: ProductRackFragment[]
}

const MapContainer = dynamic(() => import('./MapContainer').then(mod => mod.MapContainer) as Promise<ComponentType>);


type MapState = 'loading' | 'loaded' | 'default';

const ProductRackMapInner : React.FunctionComponent<{
    racks: ProductRackFragment[]
}> = ({racks}) => {
    const localization = useLocalization();
    const translate = useTranslate();
    
    const fetchVendors = useFetchProductRackVendors();
    const mapDefaultCenter = React.useMemo(() => getMapCenter(localization.locale), [localization.locale])
    const mapRef = React.useRef<GoogleMap | null>(null);
    
    const [mapState, setMapState] = React.useState<MapState>('default');
    const [selectedItem, setSelectedItem] = React.useState<VendorFragment | undefined>(undefined);
    const onChangePosition = React.useCallback(async (position: GeolocPosition | undefined) => {

        // Load data from backend
        const vendors = await fetchVendors(position?.location.lat, position?.location.lng, racks.map(r => r.id));
        if(!vendors)
        {
            setMapState('default');
            return;
        }

        // Update map with data from backend
        vendors.filter(v => v.gpsLat && v.gpsLng).forEach(vendor => {

            const data = localization.extractLocale(vendor.locales);
            if(!data)
            {
                return;
            }

            const marker = new google.maps.Marker({
                position: { lat: vendor.gpsLat!, lng: vendor.gpsLng! },
                map: mapRef.current?.state.map,
                icon: '/marker-cart.svg',
                title: data.title
            });

            marker.addListener('click', () => {
                setSelectedItem(vendor);
            });
        });

        // Change map location and zoom
        if(mapRef.current && position)
        {
            mapRef.current.state.map?.setCenter(position.location);
            mapRef.current.state.map?.setZoom(11);

            new google.maps.Marker({
                position: position.location,
                map: mapRef.current.state.map,
                icon: makeMapMarker()
            });
        }

        // Change state to loaded
        setMapState('loaded');
    }, [setMapState]);

    const geoloc = useGeolocPosition({
        onChangePosition,
        onDeniedPosition: () => onChangePosition(undefined)
    });

    React.useEffect(() => {
        if(mapState == 'loading')
        {
            // Request location permission
            if(geoloc.queryPosition)
            {
                geoloc.queryPosition();
            }
            else 
            {
                onChangePosition(undefined);
            }

            // The rest of the loading is handled by the onChangePosition handler
        }
    }, [mapState, setMapState]);

    React.useEffect(() => {
        if (selectedItem && mapRef.current) {
            mapRef.current.state.map?.panTo({
                lat: Number(selectedItem.gpsLat),
                lng: Number(selectedItem.gpsLng),
            })
        }
    }, [selectedItem])

    return <div className={classNames('productRackMap-map', mapState == 'loading' && 'productRackMap-map--loading')}>
        {
            mapState != 'loaded' && <>
                <NextImage className='productRackMap-map-background' objectPosition="center center" objectFit="cover" layout="fill" src="/map.jpg" alt="Mapa" />
                <div className='productRackMap-map-overlay'>
                    <Button loading={mapState == 'loading'} disabled={mapState == 'loading'} onClick={() => setMapState('loading')} icon={<SvgArrowDown/>}>{ translate('productRacks-showMap') }</Button>
                    <p>
                        <small>{ translate('productRacks-showMap-sub') }</small>
                    </p>
                </div>
            </>
        }

        {
            mapState != 'default' &&
            <GoogleMap
                ref={mapRef}
                mapContainerClassName="productRackMap-map-in"
                zoom={8}
                options={getMapOptions()}
                center={mapDefaultCenter}
            >
                <VendorInfoWindow
                    onClose={() => setSelectedItem(undefined)}
                    place={selectedItem}
                />
            </GoogleMap>
        }
        
    </div>
}

export const ProductRackMap: React.FunctionComponent<ProductRackMapProps> = ({
	racks
}) => {

    return <div className="productRackMap">
        <ProductRackList racks={racks} mode='vendors' />
        <MapContainer>
            <ProductRackMapInner racks={racks} />
        </MapContainer>
    </div>;
}
